/**
 * Workforce agents — the `agent_config` contract (BAL-8063 derivation rework).
 *
 * A `workforce_task_definitions` row carrying `agent_config` IS the agent assignment
 * (no registry — the BYO-agent registry returns at P6); the config freezes onto
 * `workforce_tasks.agent_config` at creation. Both jsonb columns are typed
 * `.$type<WorkforceAgentConfig>()` against THIS schema, so the DB shape, the API
 * contract, and any FE surface can never drift.
 *
 * Semantics:
 * - `execution` is the definition's REQUESTED ceiling — the server-side policy floor
 *   clamps it at dispatch/claim time (adverse-effect action classes are never 'auto',
 *   whatever the config asks for). Config is a request, policy is the law.
 * - `tools` and `limits.maxSteps` are ENFORCED as of P1-12 (2026-08-02 update): `tools`
 *   is an allowlist the judgment loop filters the offered bundle by (absent = full curated
 *   bundle; present = intersection; [] = nothing — a narrowing knob, never a granting one),
 *   and `limits.maxSteps` caps the loop. Writing them is honored. The SHAPE now enforces that
 *   too (review-fleet #25/30): `tools` is typed against WORKFORCE_AGENT_TOOL_NAMES and `limits`
 *   is a closed object, so neither a mistyped tool nor an unread limit knob can be written at
 *   all — the evil-fallbacks doctrine (no silently-dead knobs) is a schema guarantee, not a
 *   comment. The per-field comments below used to say the opposite of this paragraph; they
 *   were stale, and two seeds were already writing `tools`.
 *
 * TypeBox (not Zod like the `workforce-task-automation.ts` siblings) is deliberate: `agent_config`
 * is a unified-api concern — it types the jsonb columns in unified-api's Drizzle `schema.ts`
 * (`.$type<WorkforceAgentConfig>()`) and its write-time validation will register directly with
 * Fastify (unified-api's TypeBox provider) with no z→TypeBox bridge. The Zod siblings serve the
 * workflows-service (Zod) surface. The `execution` domain is the shared enum either way.
 */

import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';

import {
  WORKFORCE_AGENT_ARCHETYPES,
  WORKFORCE_AGENT_SKILL_KEYS,
  WORKFORCE_AGENT_TOOL_NAMES,
} from '../consts/workforce-agents';
import { WORKFORCE_AGENT_EXECUTIONS } from '../consts/workforce-task-automation';

export const WorkforceAgentConfigSchema = Type.Object(
  {
    // Contract version — bump on breaking shape changes; consumers gate on it.
    version: Type.Literal(1),
    // Requested execution ceiling ('forbidden' | 'propose' | 'auto') — clamped by the
    // server-side policy floor; adverse-effect actions never run 'auto'.
    execution: Type.Union(WORKFORCE_AGENT_EXECUTIONS.map(value => Type.Literal(value))),
    // Optional capability-class label ('judgment' | 'collection' | 'comms').
    archetype: Type.Optional(
      Type.Union(WORKFORCE_AGENT_ARCHETYPES.map(value => Type.Literal(value))),
    ),
    // ENFORCED (P1-12) — a NARROWING tool allowlist: the loop offers `bundle ∩ tools`.
    // Absent = the full curated bundle; `[]` = no tools at all. Typed against the canonical
    // vocabulary so a typo is a write-time validation error, not a silently smaller toolset
    // (review-fleet #25: `'screenshoot'` used to intersect to nothing and fail open-ish).
    tools: Type.Optional(
      Type.Array(Type.Union(WORKFORCE_AGENT_TOOL_NAMES.map(name => Type.Literal(name)))),
    ),
    // ENFORCED (BAL-8217) — the reusable PROCEDURES this task's agent is given, resolved to bodies
    // and loaded into the prompt tail at run start. Narrowing like `tools`, with one deliberate
    // asymmetry: absent means NONE, not "all". A skill is a wall of text, so an implicit "all"
    // would be a context bomb that grows silently every time someone adds a skill.
    //
    // Typed against the closed vocabulary for the same reason `tools` is (review-fleet #25). Note
    // this is a TYPE-level guarantee only: nothing validates `agent_config` on write today (this
    // schema's sole runtime use in unified-api is the tasks-read RESPONSE), and seeds write the
    // jsonb directly. A bad key is caught at RUN START by `resolveSkills`, loudly.
    skills: Type.Optional(
      Type.Array(Type.Union(WORKFORCE_AGENT_SKILL_KEYS.map(key => Type.Literal(key)))),
    ),
    // ENFORCED (P1-12) — `maxSteps` caps the investigation loop. CLOSED on purpose: an open
    // `Record<string, number>` accepted knobs nothing reads, which is exactly the
    // silently-dead-config the evil-fallbacks doctrine forbids. A new limit is a code change
    // (add it here AND to the code that honours it), never just a config write.
    limits: Type.Optional(
      Type.Object(
        { maxSteps: Type.Optional(Type.Integer({ minimum: 1 })) },
        { additionalProperties: false },
      ),
    ),
  },
  { additionalProperties: false },
);

export type WorkforceAgentConfig = Static<typeof WorkforceAgentConfigSchema>;
